import { DOCUMENT } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  AnalyticsService,
  CustomDimensions,
  ResponsiveService,
  WindowService,
} from '@frk/eds-components';
import { InsightsService } from '@marketing/insights/insights.service';
import { getMonthDiffrence } from '@marketing/marketing-utils';
import { WINDOW } from '@ng-web-apis/common';

import {
  AppStateService,
  GlobalConfigService,
  IsLoginUser,
  LocalLinkService,
  ProfileService,
  SegmentService,
  SiteConfigService,
  WidenService,
  WindowScrollService,
} from '@services';
import { AbstractBaseComponent } from '@shared/abstract-base/abstract-base.component';
import { SiteParams } from '@types';
import { Logger } from '@utils/logger';
import { combineLatest, of, Subject, Subscription } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { LinkService } from '@services/link.service';
import {
  AlternativeArticle,
  ArticleContent,
  PageContents,
} from './article.types';
import get from 'lodash/get';
import { ArticleService } from './services/article.service';

/**
 * Logger
 */
const logger = Logger.getLogger('ArticleComponent');

/**
 * The Article component can be used to display article page
 *
 * Selector: `ft-article-blocks`
 *
 * Exported as: `ArticleComponent`
 */
@Component({
  selector: 'ft-article-blocks',
  templateUrl: './article.component.html',
  styleUrls: ['./article.component.scss'],
})
export class ArticleComponent
  extends AbstractBaseComponent
  implements OnDestroy, OnInit, AfterViewInit {
  // Public Fields
  showButtons = false;
  sidebarOffsetTop: number;
  sidebarHeight: number;
  translationLabel: any;
  relatedContentHeaderTypeLabel: any;
  showArticleInfo: boolean;
  content: ArticleContent;
  isContactAvailable: boolean;
  segmentTags: string;
  pageTheme: string;
  buttontype: string;
  desktopImage: string;
  mobileImage: string;
  insightsResource: any;
  jumplinkDet: PageContents = {
    jumplinks: [],
  };
  compactdet = false;
  investmentTeamData: string;
  siteParams: SiteParams;
  simLogoResourceBundle: any;
  simLogoLinkResourceBundle: object;
  public isSingleCard = false;
  public segmentOk = false;

  // Element Refs
  @ViewChild('sidebar') sidebar: ElementRef;

  // Observables
  isMobile$: Subscription;
  isMobile: boolean;
  isHandheld$: Subscription;
  isHandheld: boolean;
  isfragement$: Subscription;

  // Private fields
  private unsubscribe$: Subject<void> = new Subject<void>();

  /**
   * Constructor
   */
  constructor(
    private responsiveService: ResponsiveService,
    private renderer2: Renderer2,
    @Inject(DOCUMENT) private doc: Document,
    @Inject(WINDOW) private windowRef: Window,
    private widenService: WidenService,
    private appStateService: AppStateService,
    private segmentService: SegmentService,
    private insightsService: InsightsService,
    private router: ActivatedRoute,
    private route: Router,
    private analytics: AnalyticsService,
    private siteConfig: SiteConfigService,
    private profileService: ProfileService,
    private locallinkService: LocalLinkService,
    private pageConfigService: GlobalConfigService,
    private scrollService: WindowScrollService,
    private ctaLinkService: LinkService,
    private articleService: ArticleService,
    private windowService: WindowService,
    private cdr: ChangeDetectorRef
  ) {
    super();
    this.siteParams = this.pageConfigService.getSiteParams();
    this.segmentTags = this.segmentService.getCurrentSegment()?.insightAudiences;
  }
  ngOnInit(): void {
    super.ngOnInit();

    // Set breadcrumbs
    this.setBreadcrumbItems();

    // Responsive behaviours
    this.isMobile$ = this.responsiveService
      .isMobile$()
      ?.subscribe((isMobile) => (this.isMobile = isMobile));
    this.isHandheld$ = this.responsiveService
      .isHandheld$()
      ?.subscribe((isHandheld) => (this.isHandheld = isHandheld));
    if (this.isMobile) {
      this.showButtons = true;
      this.compactdet = true;
    }

    // Translations and labels
    this.translationLabel = this.component?.getModels()['ft.core.article'];
    this.relatedContentHeaderTypeLabel = this.component?.getModels()[
      'ft.core.article-related-content'
    ];
    this.simLogoResourceBundle = this.component?.getModels()[
      'ft.core.sim-logos'
    ];
    this.simLogoLinkResourceBundle = this.component?.getModels()[
      'ft.core.sim-logos-links'
    ];

    // Load content from resourceapi
    this.content = this.getContent();

    // Add Schema (TODO: Move to the SchemaService)
    this.createArticleSchema();

    // Set Meta Tags
    const metadata = get(this.page, ['model', 'page', 'models', 'pageData']);
    const imageUrl = this.articleService.getSocialMediaImg(this.content);
    this.articleService.setMetaTags(metadata, imageUrl, this.content?.title);

    // NGC-13630 : Removed HostListener and added windowService
    this.windowService
      .getScrollObs$(100)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        if (this.isMobile) {
          return;
        }
        const windowScroll = this.windowRef.pageYOffset;
        this.sidebarOffsetTop = this.sidebar?.nativeElement.offsetTop;
        this.sidebarHeight = this.sidebar?.nativeElement.offsetHeight;
        this.showButtons =
          windowScroll > this.sidebarOffsetTop + this.sidebarHeight;
        this.cdr.detectChanges();
      });

    // Trigger Pageview
    this.triggerPageView();
  }

  /**
   * Angular hook
   */
  ngAfterViewInit(): void {
    try {
      this.scrollService.scrollToAnchor(this.router.snapshot.fragment);
    } catch (e) {}
  }

  /**
   * Angular hook
   */
  ngOnDestroy() {
    this.isMobile$?.unsubscribe();
  }

  /**
   * Get content from document
   */

  getContent(): ArticleContent {
    const article = this.document?.getData<ArticleContent>();

    // TODO: Remove this line once rightRails has been identified
    logger.debug(article);

    if (article) {
      // Content type specific logic
      if (article?.contentType) {
        switch (article.contentType) {
          case 'ftcore:Article':
            this.pageTheme = 'primary';
            this.buttontype = 'secondary-dark';
            break;
          case 'ftcore:PressRelease':
            this.pageTheme = 'highlight-blue';
            this.buttontype = 'secondary';
            break;
          default:
            this.pageTheme = 'primary';
            this.buttontype = 'tertiary';
        }
      }
      // Set archive flag
      if (!article?.doNotArchive) {
        if (getMonthDiffrence(article?.isodate) > 15) {
          article.isArchive = true;
        }
      }
      // Set archive label based on flag
      if (article?.isArchive && !article?.hideDate) {
        article.archiveLabel = ' - ' + this.translationLabel?.archive;
      } else if (article?.isArchive && article?.hideDate) {
        article.archiveLabel = this.translationLabel?.archive;
      } else {
        article.archiveLabel = '';
      }
      // Set isodate
      if (article?.isodate) {
        article.isodate = this.insightsService.getLongDate(article.isodate);
      }
      // Set Article Banner Image
      if (article?.image?.widenDocument) {
        article.imgSrc = this.widenService.getWidenImageVariantUrl(
          this.widenService.getWidenAssetUrl(article.image?.widenDocument),
          'original',
          'webp',
          'eds-banner-article-insights'
        );
      } else {
        // fallback for empty image source
        article.imgSrc = 'javascript:void(0)';
      }

      // Set local pdf
      if (article?.localizablePdf?.length) {
        this.articleService.setLocalizablePdf(this.segmentTags, article);
      }
      // Widen Document
      if (article?.audio?.widenDocument) {
        article.audioHref = this.widenService.getWidenAssetUrl(
          article.audio?.widenDocument
        );
      }
      // Widen Asset link
      if (article?.linkWidenAsset) {
        article.pdf = [
          {
            displayText: article.linkWidenAsset?.displayText,
            href: this.widenService.getWidenAssetUrl(
              article.linkWidenAsset?.widenAssetCompound?.widenDocument
            ),
            isNewWindow: article.linkWidenAsset?.target === '_blank',
          },
        ];
        logger.debug(article.pdf);
      }

      // Contributors & Authors
      if (article?.insightMetadata?.contributors) {
        this.articleService.formatContributors(article, this.page);
        this.articleService.formatAuthors(article, this.page);
      }

      // Format metadata
      this.articleService.formatMetadata(article);

      // Alternative content format
      if (article?.insightMetadata?.alternativeContent) {
        let alternativeArticleData: AlternativeArticle;
        alternativeArticleData = this.page
          .getContent(article.insightMetadata.alternativeContent)
          .getData<AlternativeArticle>();
        alternativeArticleData.link = this.page
          .getContent(article?.insightMetadata?.alternativeContent)
          .getUrl();
        article.alternativeArticle = alternativeArticleData;
      }

      // Sims logo
      if (article?.insightMetadata?.logo) {
        article.SIM = this.insightsService.getSIMLogo(
          article?.insightMetadata?.logo,
          false,
          this.simLogoResourceBundle
        );
        if (this.simLogoLinkResourceBundle) {
          article.SIM.logoHref = this.simLogoLinkResourceBundle[
            `logo-${article?.insightMetadata?.logo}`
          ];
        }
      }
      if (article?.audienceExclusions) {
        this.segmentOk = this.articleService.isSegmentTagAvailable(
          this.segmentTags,
          JSON.stringify(article?.audienceExclusions)
        );
      } else {
        this.segmentOk = false;
      }
      // General content formatting
      if (article?.content) {
        article.contentBlocks = [];
        for (const contentBlock of article.content) {
          // Localizable card
          if (contentBlock?.cardCompound) {
            if (contentBlock?.cardCompound.length > 0) {
              contentBlock?.cardCompound.forEach((card, i) => {
                this.getSingleCardValue(card);
                contentBlock.cardCompound[i] = this.formatCard(card);
              });
            } else {
              this.getSingleCardValue(contentBlock.cardCompound);
              contentBlock.cardCompound = this.formatCard(
                contentBlock.cardCompound
              );
            }

            // Check should we show it
            if (contentBlock.targeting) {
              contentBlock.isAllowed = this.articleService.isSegmentTagAvailable(
                this.segmentTags,
                contentBlock.targeting
              );
            } else {
              contentBlock.isAllowed = true;
            }

            if (contentBlock.isAllowed) {
              contentBlock.spacingBottom = contentBlock.spacingBottom || 'xl';
            } else {
              contentBlock.spacingBottom = 'none';
            }
          }

          this.mobileImage = contentBlock?.mobileImage?.widenDocument;
          this.desktopImage = contentBlock?.widenAsset;
          if (this.isMobile && contentBlock?.mobileImage?.widenDocument) {
            contentBlock.widenAsset = this.mobileImage;
          } else {
            contentBlock.widenAsset = this.desktopImage;
          }

          if (contentBlock.type === 'ParagraphBlock') {
            contentBlock.content = this.locallinkService.getLocalizeLinks(
              contentBlock.content
            );
            contentBlock.spacingBottom = contentBlock.spacingBottom || 'none';
          }

          article?.contentBlocks.push(contentBlock);
        }
      }

      // Format jumplinks
      if (article?.jumpLinks?.length) {
        const updateUrl = this.route.createUrlTree([], {
          relativeTo: this.router,
        });
        for (const element of article.jumpLinks) {
          this.jumplinkDet.jumplinks.push({
            id: updateUrl + '#' + element.anchor,
            title: element.heading,
          });
        }
      }

      // Article Related Content
      if (article?.relatedContent?.length) {
        this.articleService.getRelatedContent(
          this.segmentTags,
          article,
          this.page
        );
      }

      // Display cards format
      if (article?.displayInsightsCards) {
        // Check for cms title override
        const title = this.relatedContentHeaderTypeLabel[
          article?.insightsCardsHeader
        ];

        article.insightsCardsFilters.displayStyle = 'ArticleCards';
        article.insightsCardsFilters.preTitle = this.translationLabel?.insights;
        article.insightsCardsFilters.title = title
          ? title
          : this.translationLabel?.relatedArticle;
        article.insightsCardsFilters.enableViewMore = article
          .insightsCardsFilters.hideViewMoreLink
          ? false
          : true;
        this.insightsResource = this.component?.getModels()[
          'ft.core.insightsCards'
        ];
      }
      if (
        article?.company ||
        article?.contactName ||
        article?.contactTelephone
      ) {
        this.isContactAvailable = true;
      }

      // Set showArticleInfo flag
      if (
        article?.insightMetadata?.logo ||
        article?.alternativeArticle ||
        article?.contributors?.length ||
        article?.sections?.length ||
        this.isContactAvailable
      ) {
        this.showArticleInfo = true;
      }
      // still righRail type is not defined as each element of an array is of different type
      if (article?.rightRail) {
        article?.rightRail?.forEach((data) => {
          data.spacingBottom = data?.spacingBottom || 'none';

          if (data.style === 'Promo Card' || data.style === 'Media Card') {
            data.cardCompound.forEach((card) => {
              card.imageSrc = this.widenService.getWidenAssetUrl(
                card?.image?.widenAsset
              );
            });
          }
          if (data.type === 'ParagraphBlock') {
            data.content = this.locallinkService.getLocalizeLinks(data.content);
          }
        });
      }
    }
    return article;
  }

  /**
   * This method transforms the content returned from bloomreach
   * into the format expected in the EDS component.
   *
   * DUPLICATE CODE!! - MOVE TO UTILITY
   *
   * @param card object returned from the document parameter
   */
  formatCard(card) {
    card.body = card.content;

    // Use link collection if it exists (seems to be a difference between compounds)
    if (card.linkCompound?.linkCollection?.length) {
      card.linkCompound = card.linkCompound.linkCollection[0];
    } else if (card.link?.linkCollection?.length) {
      card.linkCompound = card.link.linkCollection[0];
    }

    card.ctaText = card.linkCompound?.displayText;
    card.href = this.ctaLinkService.getCTALink(
      this.page,
      card.linkCompound,
      this.appStateService?.getHomePageUrl()
    );
    card.signInRequired = card.linkCompound?.signInLink;
    card.externalLink = card.linkCompound?.external;
    card.externalSuffix = card.linkCompound?.externalSuffix;
    card.isNewWindow = card.linkCompound?.target === '_blank' ? true : false;
    // End link formatting

    // Set card theme
    card.theme = this.theme || 'primary';

    // Set widen asset
    if (card.image) {
      card.imageSrc = this.widenService.getWidenAssetUrl(card.image.widenAsset);
      card.imageSrc = this.widenService.getWidenImageVariantUrl(
        card.imageSrc,
        'original',
        'webp'
      );
    }

    return card;
  }

  /**
   * Trigger Page View event using the AnalyticsService
   */
  private triggerPageView(): void {
    const articleData$ = of(this.content);
    // prevent duplicate pageview event from firing.
    combineLatest([articleData$, this.profileService.getUserProfile$()])
      .pipe(take(1), takeUntil(this.unsubscribe$))
      .subscribe(([articleData, profileData]) => {
        const pageTitle = this.page.getComponent()?.getModels()?.pageData
          ?.title;
        const commonParams = {
          title: pageTitle,
          url: this.route.url,
        };

        if (articleData?.investmentteam) {
          this.investmentTeamData = articleData?.investmentteam?.join('|');
        } else {
          this.investmentTeamData = 'other';
        }

        const customDimensions: CustomDimensions = {
          article_id: articleData?.articleID,
          page_location: this.route.url,
          page_title: pageTitle,
          page_type: this.page.getComponent()?.getModels()?.pageData?.pageType,
          page_category: this.page.getComponent()?.getModels()?.pageData
            ?.pageType,
          language_code: this.page.getComponent()?.getModels()?.pageData
            ?.language,
          publish_date: articleData?.isodate,
          investment_theme: articleData?.investmenttheme?.join('|'),
          investment_team: this.investmentTeamData,
          publication_series: articleData?.publicationseries?.join('|'),
          specialty: articleData?.speciality?.join('|'),
          article_title: this.page.getComponent()?.getModels()?.pageData?.title,
          segmentation: articleData?.audienceExclusions?.join('|'),
          author: articleData?.authors?.join('|'),
          role_type_track: profileData.profileInfo.role,
          role_selected: profileData?.profileInfo?.role,
          asset_class: articleData?.assetclassPage?.join('|'),
          logged_in_status: profileData?.isLoggedIn
            ? IsLoginUser.LOGGEDIN
            : IsLoginUser.LOGGEDOUT,
          is_internal_user: String(
            this.profileService.checkIfUserIsInternal(
              profileData?.profileInfo?.userGroup
            )
          ),
          country_code: this.siteConfig?.product?.site?.country,
          web_experience: profileData.profileInfo.webExperience,
        };
        this.analytics.pageViewWithCustomDimensions(
          customDimensions,
          commonParams
        );
      });
  }

  /**
   * create article schema (TODO: Move to schemaService)
   */
  createArticleSchema() {
    let authors = '';

    if (this.content?.contributors) {
      for (const author of this.content.contributors) {
        authors += '{"@type": "Person","name":"' + author.fullName + '"},';
      }
    }

    const script = this.renderer2.createElement('script');
    script.type = `application/ld+json`;
    script.text =
      `{
      "@context":"https://schema.org"
      "@type":"Article",
      "mainEntityOfPage": {
        "@type":"WebPage",
        "title":"` +
      this.content?.title +
      `",
        "datePublished":"` +
      this.content?.isodate +
      `",
        "image":"` +
      this.content?.imgSrc +
      `",
        "author":[` +
      authors +
      `]}
    }`;
    this.renderer2.appendChild(this.doc.head, script);
  }

  /**
   * Calculates value of singleCard that is passed to eds-component from template file
   * @param cardCompound object of cardCompound
   */
  public getSingleCardValue(cardCompound) {
    this.isSingleCard = cardCompound?.image?.widenAsset ? false : true;
  }
}
